'use client';

import { Box, Button, Flex, FlexProps, Text } from '@chakra-ui/react';
import { Background, BackgroundVariant, ReactFlow } from '@xyflow/react';
import { memo, useMemo } from 'react';

import { useColorMode } from '@components/ui/color-mode';
import AddButtonNode from '@features/create/nodes/AddButtonNode';
import CommodityNode from '@features/create/nodes/CommodityNode';
import RootNode from '@features/create/nodes/RootNode';
import { Block, UpdateBlock } from '@features/create/types';
import { useSimpleTreeLayout } from '@features/create/useSimpleTreeLayout';
import '@xyflow/react/dist/base.css';
import { toPxString } from 'helpers/styles';
import { useStores } from 'stores/rootStore';

export type SignalType = 'demand';

const CONFIRM_LABELS: Record<SignalType, string> = {
  demand: 'Confirm request',
};

interface HeaderProps {
  height: FlexProps['height'];
  signalType: SignalType;
}

const Header = ({ height, signalType }: HeaderProps) => {
  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      h={height}
      borderBottomColor="border.light"
      borderBottomWidth="1px"
      px={3}
      flexShrink={0}
    >
      <Flex gap={4}>
        <Text fontWeight="medium">Let&apos;s handle your procurement requests</Text>
      </Flex>
      <Flex gap={2}>
        <Button borderRadius="lg" size="2xs" hideFrom="lg" variant="secondaryOutline">
          Reset
        </Button>
        <Button borderRadius="lg" size="2xs" hideFrom="lg">
          <Box hideBelow="sm">{CONFIRM_LABELS[signalType]}</Box>
          <Box hideFrom="sm">Confirm request</Box>
        </Button>
      </Flex>
    </Flex>
  );
};

interface Props {
  headerHeight: string;
  blocks: Block[];
  addBlock: () => void;
  updateBlock: (blockId: string, update: UpdateBlock) => void;
  removeBlock: (blockId: string) => void;
  signalType: SignalType;
}

const CreateSignal = ({
  headerHeight,
  blocks,
  addBlock,
  updateBlock,
  removeBlock,
  signalType,
}: Props) => {
  const { colorMode } = useColorMode();
  const { pageStore } = useStores();
  const nodeTypes = useMemo(
    () => ({
      root: RootNode,
      commodity: CommodityNode,
      addButton: AddButtonNode,
    }),
    [],
  );

  const { nodes, edges, maxHeight } = useSimpleTreeLayout({
    blocks,
    addBlock,
    updateBlock,
    removeBlock,
    openSearch: pageStore.openSearchModal,
    signalType,
  });

  return (
    <Flex direction="column" h="full" w="full">
      <Header height={headerHeight} signalType={signalType} />
      <Flex w="full" h="full" overflow="auto" id="react-flow-container">
        <Flex minH={toPxString(maxHeight)} w="full" id="react-flow-wrapper">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            colorMode={colorMode}
            nodesDraggable={false}
            nodesConnectable={false}
            panOnDrag={false}
            zoomOnScroll={false}
            zoomOnDoubleClick={false}
            zoomOnPinch={false}
            preventScrolling={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          </ReactFlow>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default memo(CreateSignal);
